import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { jobVacancyService } from '../services/jobVacancyService';
import { JobVacancy, VacancyStatus, VacancyStats, VACANCY_STATUS_LABELS, WORK_MODEL_LABELS, SENIORITY_LABELS } from '../types/jobVacancy';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { VacancyStatusChip } from '../components/VacancyStatusChip';
import { VacancyStatsCard } from '../components/VacancyStatsCard';
import HireDialog from '../components/recruitment/HireDialog';

export const VacancyManagement: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [vacancies, setVacancies] = useState<JobVacancy[]>([]);
    const [stats, setStats] = useState<VacancyStats | null>(null);
    const [selectedVacancy, setSelectedVacancy] = useState<JobVacancy | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [hireOpen, setHireOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<VacancyStatus>(VacancyStatus.DRAFT);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [vacanciesData, statsData] = await Promise.all([
                jobVacancyService.getVacancies(),
                jobVacancyService.getVacancyStats()
            ]);
            setVacancies(vacanciesData);
            setStats(statsData);
        } catch (error) {
            enqueueSnackbar('Erro ao carregar vagas', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, vacancy: JobVacancy) => {
        setSelectedVacancy(vacancy);
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedVacancy) {
            navigate(`/vagas/${selectedVacancy.id}/edit`);
        }
    };

    const handleDelete = () => {
        handleMenuClose();
        setDeleteDialogOpen(true);
    };

    const handleStatusChange = () => {
        handleMenuClose();
        if (selectedVacancy) setNewStatus(selectedVacancy.status);
        setStatusDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedVacancy) return;

        try {
            await jobVacancyService.deleteVacancy(selectedVacancy.id);
            enqueueSnackbar('Vaga excluída com sucesso', { variant: 'success' });
            loadData();
        } catch (error) {
            enqueueSnackbar('Erro ao excluir vaga', { variant: 'error' });
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const handleStatusConfirm = async () => {
        if (!selectedVacancy) return;

        try {
            await jobVacancyService.updateVacancyStatus(selectedVacancy.id, newStatus);
            enqueueSnackbar('Status atualizado com sucesso', { variant: 'success' });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.error || 'Erro ao atualizar status', { variant: 'error' });
        } finally {
            setStatusDialogOpen(false);
        }
    };

    const handleViewApplications = () => {
        handleMenuClose();
        if (selectedVacancy) {
            navigate(`/vagas/${selectedVacancy.id}/candidaturas`);
        }
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 4 }}>
                <Grid container spacing={3}>
                    {/* Header */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                                Gestão de Vagas
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/vagas/nova')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'common.white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark'
                                    }
                                }}
                            >
                                Nova Vaga
                            </Button>
                        </Box>
                    </Grid>

                    {/* Stats Cards */}
                    {stats && (
                        <Grid item xs={12}>
                            <VacancyStatsCard stats={stats} />
                        </Grid>
                    )}

                    {/* Vacancies Table */}
                    <Grid item xs={12}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cargo</TableCell>
                                        <TableCell>Tipo de Contrato</TableCell>
                                        <TableCell>Salário Base</TableCell>
                                        <TableCell>Localização</TableCell>
                                        <TableCell>Modelo de Trabalho</TableCell>
                                        <TableCell>Senioridade</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Candidaturas</TableCell>
                                        <TableCell>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vacancies.map((vacancy) => (
                                        <TableRow key={vacancy.id}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body1">
                                                        {vacancy.role || '-'}
                                                    </Typography>
                                                    {vacancy.project_name && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {vacancy.project_name}{vacancy.is_public ? ' · pública' : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{vacancy.contract_type || '-'}</TableCell>
                                            <TableCell>
                                                {vacancy.base_salary ? 
                                                    new Intl.NumberFormat('pt-BR', { 
                                                        style: 'currency', 
                                                        currency: 'BRL' 
                                                    }).format(vacancy.base_salary) 
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>{vacancy.location || '-'}</TableCell>
                                            <TableCell>{(vacancy.work_model && WORK_MODEL_LABELS[vacancy.work_model]) || vacancy.work_model || '-'}</TableCell>
                                            <TableCell>{(vacancy.seniority && SENIORITY_LABELS[vacancy.seniority]) || vacancy.seniority || '-'}</TableCell>
                                            <TableCell>
                                                <VacancyStatusChip status={vacancy.status} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {vacancy.total_applications} candidatos
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={(e) => handleMenuOpen(e, vacancy)}
                                                    size="small"
                                                    aria-label={`Ações da vaga ${vacancy.role || vacancy.id}`}
                                                    sx={{
                                                        color: 'primary.main',
                                                        backgroundColor: 'background.paper',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Box>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewApplications}>Ver Candidaturas</MenuItem>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleStatusChange}>Alterar Status</MenuItem>
                {selectedVacancy && [VacancyStatus.OPEN, VacancyStatus.IN_PROGRESS].includes(selectedVacancy.status) && (
                    <MenuItem onClick={() => { handleMenuClose(); setHireOpen(true); }}>Fechar com contratação</MenuItem>
                )}
                <MenuItem onClick={handleDelete}>Excluir</MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir esta vaga? Esta ação não pode ser desfeita.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <LoadingButton
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <HireDialog
                open={hireOpen}
                vacancyId={selectedVacancy?.id ?? null}
                vacancyLabel={selectedVacancy ? [selectedVacancy.role, selectedVacancy.project_name].filter(Boolean).join(' · ') : ''}
                onClose={() => setHireOpen(false)}
                onHired={(message) => {
                    setHireOpen(false);
                    enqueueSnackbar(message, { variant: 'success', autoHideDuration: 8000 });
                    loadData();
                }}
            />

            {/* Status Change Dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
                <DialogTitle>Alterar Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Novo Status</InputLabel>
                        <Select
                            value={newStatus}
                            label="Novo Status"
                            onChange={(e) => setNewStatus(e.target.value as VacancyStatus)}
                        >
                            {/* "Fechada" só pela contratação (precisa saber quem entrou no time) */}
                            {Object.values(VacancyStatus).filter((s) => s !== VacancyStatus.CLOSED || selectedVacancy?.status === VacancyStatus.CLOSED).map((status) => (
                                <MenuItem key={status} value={status}>
                                    {VACANCY_STATUS_LABELS[status]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancelar</Button>
                    <LoadingButton
                        onClick={handleStatusConfirm}
                        color="primary"
                        variant="contained"
                    >
                        Confirmar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Container>
    );
}; 